import { useCallback } from "react";
import { useSelector } from "react-redux";
import { apiUrl } from "Constants";
import MplusApi from "Services/Mplus";

// Messages lisibles pour les codes d'erreur de l'API M+ (section 14).
const ERROR_MESSAGES = {
  FORBIDDEN: "Votre rôle ne permet pas cette action.",
  INVALID_OTP: "Code SMS incorrect.",
  OTP_EXPIRED: "Code SMS expiré : demandez-en un nouveau.",
  OTP_COOLDOWN: "Patientez avant de redemander un code.",
  OTP_SEND_LIMIT: "Trop de codes envoyés : réessayez plus tard.",
  OTP_TOO_MANY_ATTEMPTS: "Trop de tentatives : demandez un nouveau code.",
  RATE_LIMITED: "Trop de demandes : patientez quelques instants.",
  VALIDATION_ERROR: "Un champ est manquant ou invalide.",
  LEASE_PURCHASE_FORBIDDEN: "Vous n'avez pas accès à cette demande.",
  MPLUS_NOT_ELIGIBLE: "Demandeur non éligible : renseignez un motif de dérogation.",
  MPLUS_FINANCING_EXISTS: "Tous les véhicules de la demande ont déjà un dossier.",
  MPLUS_VEHICLE_UNAVAILABLE: "Ce véhicule n'est plus disponible.",
  MPLUS_VEHICLE_WRONG_MODEL: "Ce véhicule n'est pas du modèle demandé.",
  MPLUS_VEHICLE_ALREADY_RESERVED: "Ce dossier a déjà un véhicule réservé.",
  MPLUS_VEHICLE_NOT_AT_HUB: "La livraison n'est possible que depuis un hub : transférez d'abord le véhicule.",
  MPLUS_CONTRACTS_NOT_SIGNED: "Les contrats ne sont pas encore tous signés.",
  CONTRACT_NOT_VIEWED: "Ouvrez le PDF du contrat avant de demander le code de signature.",
  MPLUS_FINANCING_LOCKED: "Ce dossier ne peut plus être modifié.",
};

export const apiErrorMessage = (error, fallback) => {
  const data = error?.response?.data || {};
  if (ERROR_MESSAGES[data.code]) return ERROR_MESSAGES[data.code];
  if (error?.response?.status === 403 && !data.code) return ERROR_MESSAGES.FORBIDDEN;
  return data.error || data.message || fallback;
};

// errors: [{ field, message }] d'un VALIDATION_ERROR → { field: message }.
export const fieldErrors = (error) =>
  (error?.response?.data?.errors || []).reduce((result, item) => (item?.field ? { ...result, [item.field]: item.message } : result), {});

export const apiErrorCode = (error) => error?.response?.data?.code || "";

// Les réponses sont { data: … } ; certaines listes sont paginées { data: { data: [] } }.
export const unwrap = (response) => response?.data?.data;
export const unwrapList = (response) => {
  const data = unwrap(response);
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

export const formatAmount = (value) => {
  if (value === null || value === undefined || value === "") return "--";
  const amount = Number(value);
  return Number.isFinite(amount) ? `${new Intl.NumberFormat("fr-FR").format(Math.round(amount))} XAF` : "--";
};

// Dates affichées en JJ/MM/AAAA.
export const formatDate = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "--" : date.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

export const formatDateTime = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "--"
    : `${formatDate(value)} ${date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
};

export const toInputDate = (value) => (value ? String(value).slice(0, 10) : "");
export const todayIso = () => new Date().toISOString().slice(0, 10);

// Libellés français des énumérations de l'API.
export const FREQUENCY_LABELS = { MONTHLY: "Mensuel", WEEKLY: "Hebdomadaire" };
export const FUEL_LABELS = { FUEL: "Essence", ELECTRIC: "Électrique", HYBRID: "Hybride" };
export const TRANSMISSION_LABELS = { MANUAL: "Manuelle", AUTOMATIC: "Automatique" };
export const BODY_LABELS = { SEDAN: "Berline", SUV: "SUV", PICKUP: "Pick-up", MOTORCYCLE: "Moto", TRICYCLE: "Tricycle" };
export const DOCUMENT_STATUS = {
  RECEIVED: { label: "Reçu", tone: "pending" },
  VALIDATED: { label: "Validé", tone: "approved" },
  REJECTED: { label: "Rejeté", tone: "rejected" },
};

// Caractéristiques d'un modèle (énergie, boîte, carrosserie) en libellés lisibles.
export const productFeatures = (product) => [
  BODY_LABELS[product?.body_type],
  FUEL_LABELS[product?.fuel_type],
  TRANSMISSION_LABELS[product?.transmission],
].filter(Boolean);

// Rôles du profil connecté. ADMIN et SUPER_ADMIN ont tous les droits ;
// sans rôles connus (backend non migré), on ne masque rien : le serveur reste juge.
export const useHasRole = () => {
  const roles = useSelector((state) => state?.ProfileSettingsReducer?.profiledetails?.roles);
  return useCallback((...required) => {
    if (!Array.isArray(roles) || !roles.length) return true;
    const owned = roles.map((role) => String(role).toUpperCase());
    if (owned.includes("ADMIN") || owned.includes("SUPER_ADMIN")) return true;
    return required.some((role) => owned.includes(role));
  }, [roles]);
};

export const toAssetUrl = (path) => (!path ? "" : /^https?:\/\//i.test(path) ? path : `${apiUrl}${path}`);

// Statuts d'un dossier (section 6).
export const FINANCING_STATUS = {
  SUBMITTED: { label: "Financement à configurer", tone: "pending" },
  LOAN_CREATED: { label: "Prêt créé", tone: "pending" },
  LOAN_APPROVED: { label: "Financement validé", tone: "approved" },
  ACTIVE: { label: "Dossier actif", tone: "approved" },
  CLOSED: { label: "Dossier soldé", tone: "approved" },
  CANCELLED: { label: "Dossier annulé", tone: "rejected" },
};

const STATUS_ORDER = ["SUBMITTED", "LOAN_CREATED", "LOAN_APPROVED", "ACTIVE", "CLOSED"];
export const financingReached = (status, target) =>
  STATUS_ORDER.indexOf(status) >= STATUS_ORDER.indexOf(target) && STATUS_ORDER.indexOf(status) !== -1;

export const isSigned = (contract) => (contract?.status || "").toString().toLowerCase() === "signed";

// Le modèle du dossier : product_catalog sur le dossier, sinon celui de la demande.
export const financingProduct = (financing, application) =>
  financing?.product_catalog || application?.raw?.product_catalog || application?.raw?.vehicle || {};

export const productId = (financing, application) =>
  financing?.product_catalog_id || financing?.product_catalog?.id || application?.raw?.product_catalog_id || application?.raw?.product_catalog?.id || "";

export const repaymentCount = (financing, application) =>
  Number(financing?.number_of_repayments ?? financing?.months ?? application?.financingDuration) || null;

// Normalise une échéance Fineract et calcule le solde restant s'il manque.
export const normalizePeriods = (periods = [], principal = 0) => {
  let balance = Number(principal) || 0;
  return periods.map((period, index) => {
    const capital = Number(period.principal ?? period.principalDue ?? 0);
    const interest = Number(period.interest ?? period.interestDue ?? 0);
    balance -= capital;
    return {
      period: period.period ?? index + 1,
      dueDate: period.dueDate || period.due_date,
      principal: capital,
      interest,
      total: Number(period.total ?? period.totalDue ?? capital + interest),
      balanceAfter: Number(period.balanceAfter ?? period.balance_after ?? period.principalLoanBalanceOutstanding ?? Math.max(balance, 0)),
      paid: Boolean(period.complete ?? period.paid ?? (period.totalOutstanding === 0)),
      paidAmount: period.paid_amount != null ? Number(period.paid_amount) : null,
      outstanding: period.outstanding != null ? Number(period.outstanding) : null,
    };
  });
};

// Ouvre un contrat PDF dans un nouvel onglet (et enregistre la lecture côté serveur).
export const openContractPdf = async (contractId, version) => {
  const response = await MplusApi.downloadContract(contractId, version);
  const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
  // Si le navigateur bloque l'onglet (ouverture après un appel async), on télécharge le fichier.
  if (!window.open(url, "_blank")) {
    const link = document.createElement("a");
    link.href = url;
    link.download = `contrat-${contractId}${version ? `-v${version}` : ""}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};
